import RepositoryReleazio from "@/repositories/v2/repository-releazio/RepositoryReleazio";
import ReleazioConfigRequestDto from "@models/v2/releazio/config/ReleazioConfigRequestDto";
import ReleazioConfig from "@models/v2/releazio/config/ReleazioConfig";
import {ToRef, ref} from "vue";
import RouterService from "@/services/RouterService";
import {getDevice} from "framework7/shared/get-device";

export default class ServiceReleazio {
  private readonly _repositoryReleazio: RepositoryReleazio;
  private _initialized: boolean = false;
  private _configs: ReleazioConfig[];
  private readonly _activeConfig: ToRef<ReleazioConfig | null>;
  private _isNeedUpdatePopupOpened = false;
  private _isNeedUpdatePopupWillReopen = false;
  private _isNeedUpdate: ToRef<boolean> = ref(false);
  private _isViewedLastPost = ref(true);
  private _ramainingSkipAttempts = 0;
  private readonly _bundleId: string;
  private readonly _version: string;
  private readonly STORAGE_REMAINING_SKIP_ATTEMPTS_KEY = 'releazio_remaining_skip_attempts';
  private readonly STORAGE_INITIAL_SKIP_ATTEMPTS_KEY = 'releazio_initial_skip_attempts';
  private readonly STORAGE_IS_VIEW_POST_VERSION_KEY = 'releazio_is_view_post_version';

  public constructor(repositoryReleazio: RepositoryReleazio, version: string = "1.0.0") {
    this._repositoryReleazio = repositoryReleazio;
    this._configs = [];
    this._activeConfig = ref(null);
    this._bundleId = import.meta.env.VITE_APP_BUNDLE_ID ?? "undefined";
    this._version = version;
  }

  get initialized(): boolean {
    return this._initialized;
  }

  get remainingSkipAttempts(): number {
    return this._ramainingSkipAttempts;
  }

  set isNeedUpdatePopupWillReopen(value: boolean) {
    this._isNeedUpdatePopupWillReopen = value;
  }

  private initSkipAttempts() {
    const config = this._activeConfig.value;
    const currentVersion = this._version;
    const latestVersion = this.activeConfig.value?.appVersionCode;

    if (!config || !currentVersion || !latestVersion) return;

    const configSkipAttempts = config.skipAttempts || 0;

    if (this.isNewVersionAvailable(currentVersion, latestVersion)) {
      const localInitialAttempts = localStorage.getItem(this.STORAGE_INITIAL_SKIP_ATTEMPTS_KEY);
      const remainingSkipAttempts = localStorage.getItem(this.STORAGE_REMAINING_SKIP_ATTEMPTS_KEY);

      const needsReset = localInitialAttempts === null || parseInt(localInitialAttempts) !== configSkipAttempts;

      if (needsReset) {
        localStorage.setItem(this.STORAGE_INITIAL_SKIP_ATTEMPTS_KEY, configSkipAttempts.toString());
        localStorage.setItem(this.STORAGE_REMAINING_SKIP_ATTEMPTS_KEY, configSkipAttempts.toString());
        this._ramainingSkipAttempts = configSkipAttempts;
      } else {
        this._ramainingSkipAttempts = remainingSkipAttempts ? parseInt(remainingSkipAttempts) : configSkipAttempts;
        localStorage.setItem(this.STORAGE_REMAINING_SKIP_ATTEMPTS_KEY, this._ramainingSkipAttempts.toString());
      }
    } else {
      localStorage.removeItem(this.STORAGE_REMAINING_SKIP_ATTEMPTS_KEY);
      localStorage.removeItem(this.STORAGE_INITIAL_SKIP_ATTEMPTS_KEY);
    }
  }


  private updateSkipAttempts() {
    if (this._ramainingSkipAttempts > 0) {
      this._ramainingSkipAttempts--;
      localStorage.setItem(this.STORAGE_REMAINING_SKIP_ATTEMPTS_KEY, this._ramainingSkipAttempts.toString());
    }
  }

  public async fetchVersionConfig(lang: string) {
    const releazioConfigRequestDto = new ReleazioConfigRequestDto(this._bundleId, this._version, lang, this.getOsType());
    const {data} = await this._repositoryReleazio.fetchVersionConfig(releazioConfigRequestDto);
    this._configs = data;
    if (data.length > 0) {
      this._activeConfig.value = data[0];
      this.initSkipAttempts();
    }
    this._initialized = true;
  }

  private getOsType() {
    if (getDevice().cordova) {
      if (getDevice().ios) {
        return "ios";
      } else {
        return "android";
      }
    } else {
      return "ios";
    }
  }

  get isNeedUpdatePopupOpened(): boolean {
    return this._isNeedUpdatePopupOpened;
  }

  get isNeedUpdatePopupWillReopen(): boolean {
    return this._isNeedUpdatePopupWillReopen;
  }

  public updateViewPost() {
    const currentVersion = this.activeConfig.value?.appVersionCode;
    const storedVersion = localStorage.getItem(this.STORAGE_IS_VIEW_POST_VERSION_KEY);

    if (!currentVersion || !storedVersion) {
      this._isViewedLastPost.value = false;
      return;
    }

    this._isViewedLastPost.value = !this.isNewVersionAvailable(storedVersion, currentVersion);
  }

  public readViewPost() {
    if (this._activeConfig.value?.appVersionName) {
      localStorage.setItem(this.STORAGE_IS_VIEW_POST_VERSION_KEY, this._activeConfig.value?.appVersionName);
    }
  }

  get isViewedLastPost(): ToRef<boolean> {
    return this._isViewedLastPost;
  }

  get isNeedUpdate() {
    return this._isNeedUpdate;
  }

  get configs(): ReleazioConfig[] {
    return this._configs;
  }

  get activeConfig(): ToRef<ReleazioConfig | null> {
    return this._activeConfig;
  }

  public openNeedUpdatePopup() {
    if (!getDevice().cordova) return;
    if (this.activeConfig.value) {
      if (this.activeConfig.value.updateType === 1) return;
    }
    this.addPopupBackdrop();
    this.showNeedUpdatePopup();
    this._isNeedUpdatePopupOpened = true;
    this._isNeedUpdatePopupWillReopen = false;
  }

  private canClosePopup(): boolean {
    if (!this._activeConfig.value) return true;

    return (
      this._activeConfig.value.updateType !== 3 ||
      this._ramainingSkipAttempts > 0
    );
  }

  public closeNeedUpdatePopup(force: boolean = false) {
    if (this._activeConfig.value?.updateType === 3 && !force && !this.canClosePopup()) {
      return;
    }

    if (!force && this._activeConfig.value?.updateType === 3) {
      this.updateSkipAttempts();
    }

    this.removePopupBackdrop();
    this.hideNeedUpdatePopup();
    this._isNeedUpdatePopupOpened = false;
  }

  private showNeedUpdatePopup() {
    const el: HTMLElement | null = document.querySelector(".releazio-need-update-popup");
    if (el != null) {
      el.style.display = "block";
      setTimeout(() => {
        el.style.opacity = "1";
        el.classList.remove("hide");
      }, 314)
    }
  }

  private hideNeedUpdatePopup() {
    const el: HTMLElement | null = document.querySelector(".releazio-need-update-popup");
    if (el != null) {
      el.style.opacity = "0";
      el.classList.remove("hide");
      setTimeout(() => {
        el.style.display = "none";
      }, 314)
    }
  }

  private addPopupBackdrop() {
    const el = document.createElement("div");
    el.classList.add("releazio-need-update-popup_backdrop");
    const parent = document.getElementById("framework7-root");
    if (parent) parent.appendChild(el);
    setTimeout(() => {
      el.style.opacity = "1";
    }, 314);
  }

  private removePopupBackdrop() {
    const el: HTMLElement | null = document.querySelector(".releazio-need-update-popup_backdrop");
    if (el) {
      el.style.opacity = "0";
      setTimeout(() => {
        el.remove();
      }, 314);
    }
  }

  public checkCurrentVersionOld() {
    const currentVersion = this._version;
    const latestVersion = this.activeConfig.value?.appVersionCode;

    if (!currentVersion || !latestVersion) return;

    if (this.isNewVersionAvailable(currentVersion, latestVersion)) {
      this._isNeedUpdate.value = true;
    }
  }

  public openDeepLink() {
    if (this.activeConfig.value && this.activeConfig.value.appUrl) {
      // @ts-ignore
      window.cordova.InAppBrowser.open(this.activeConfig.value.appUrl, '_system');
    }
  }

  public hasVersionPost() {
    return this._activeConfig.value?.postUrl !== null;
  }

  public initVersionEvents(element: HTMLElement, timer: number | null, serviceRouter: RouterService) {
    if (element) {
      element.addEventListener("touchstart", () => {
        timer = setTimeout(() => {
          serviceRouter.toTechPage();
        }, 500);
      });

      element.addEventListener("mousedown", () => {
        timer = setTimeout(() => {
          serviceRouter.toTechPage();
        }, 500);
      });

      element.addEventListener("touchend", () => {
        if (timer) {
          clearTimeout(timer);
        }
      });

      element.addEventListener("mouseup", () => {
        if (timer) {
          clearTimeout(timer);
        }
      });
    }
  }

  private isNewVersionAvailable(current: string, latest: string): boolean {
    const currentParts = current.split('.').map(Number);
    const latestParts = latest.split('.').map(Number);

    const maxLength = Math.max(currentParts.length, latestParts.length);

    for (let i = 0; i < maxLength; i++) {
      const cur = currentParts[i] || 0;
      const lat = latestParts[i] || 0;

      if (lat > cur) return true;
      if (lat < cur) return false;
    }

    return false;
  }
}
